import { useMemo } from "react"

import type { MetricValueFilter } from "@/lib/metricValueFilters"
import type { TransportOperationResponse } from "@/types_api/backend/transport/operations"
import type { MetadataFilter } from "@/types/MetadataFilter"

import { useRunsPageInsightsTransport } from "../transport/useRunsPageInsightsTransport"
import { buildRunsInsightsTransportFilters } from "../utils/transportRunsFilters"

interface RunsInsightFilters {
  searchQuery?: string
  agentFilters?: string[]
  statusFilters?: string[]
  templateFilters?: string[]
  testSetFilters?: string[]
  testCaseFilters?: string[]
  personaFilters?: string[]
  metricFilters?: string[]
  metricValueFilters?: MetricValueFilter[]
  tagFilters?: string[]
  mutationFilters?: string[]
  scheduledRunId?: string
  metadataFilters?: MetadataFilter[]
  scheduledJobFilters?: string[]
  dateRange?: { start?: Date; end?: Date }
}

interface UseRunsPageInsightsOptions {
  analysisWindowDays?: 1 | 7 | 30
  enabled?: boolean
  refetchInterval?: number | false
}

interface RunsPageInsightBucket {
  date: string
  totalRuns: number
  completedRuns: number
  inProgressRuns: number
}

interface RunsPageInsightsData {
  totalRuns: number
  completedRuns: number
  inProgressRuns: number
  buckets: RunsPageInsightBucket[]
  simulationInsights?: RunsSimulationInsightsData
}

interface RunsSimulationWindowData {
  totalSimulations: number
  completedSimulations: number
  failedSimulations: number
  unscorableSimulations: number
}

interface RunsSimulationMetricInsightData {
  metricId: string
  isBinary: boolean
  currentCount: number
  currentAverage: number | null
  previousCount: number
  previousAverage: number | null
}

interface RunsSimulationPersonaMetricInsightData extends RunsSimulationMetricInsightData {
  personaId: string
}

interface RunsSimulationInsightsData {
  windowDays: 1 | 7 | 30
  current: RunsSimulationWindowData
  previous: RunsSimulationWindowData
  metrics: RunsSimulationMetricInsightData[]
  personaMetrics: RunsSimulationPersonaMetricInsightData[]
}

function mapRunsPageInsightsResponse(
  response: TransportOperationResponse<"getRunsPageInsights">,
): RunsPageInsightsData {
  const simulationInsights = response.simulation_insights
  return {
    totalRuns: response.total_runs,
    completedRuns: response.completed_runs,
    inProgressRuns: response.in_progress_runs,
    buckets: response.buckets.map((bucket) => ({
      date: bucket.date,
      totalRuns: bucket.total_runs,
      completedRuns: bucket.completed_runs,
      inProgressRuns: bucket.in_progress_runs,
    })),
    simulationInsights: simulationInsights
      ? {
          windowDays: simulationInsights.window_days,
          current: {
            totalSimulations: simulationInsights.current.total_items,
            completedSimulations: simulationInsights.current.completed_items,
            failedSimulations: simulationInsights.current.failed_items,
            unscorableSimulations: simulationInsights.current.unscorable_items,
          },
          previous: {
            totalSimulations: simulationInsights.previous.total_items,
            completedSimulations: simulationInsights.previous.completed_items,
            failedSimulations: simulationInsights.previous.failed_items,
            unscorableSimulations: simulationInsights.previous.unscorable_items,
          },
          metrics: (simulationInsights.metrics ?? []).map((metric) => ({
            metricId: metric.metric_id,
            isBinary: metric.is_binary,
            currentCount: metric.current_count,
            currentAverage: metric.current_average ?? null,
            previousCount: metric.previous_count,
            previousAverage: metric.previous_average ?? null,
          })),
          personaMetrics: (simulationInsights.segment_metrics ?? []).map(
            (metric) => ({
              metricId: metric.metric_id,
              isBinary: metric.is_binary,
              personaId: metric.segment_id,
              currentCount: metric.current_count,
              currentAverage: metric.current_average ?? null,
              previousCount: metric.previous_count,
              previousAverage: metric.previous_average ?? null,
            }),
          ),
        }
      : undefined,
  }
}

export function useRunsPageInsights(
  filters: RunsInsightFilters,
  options?: UseRunsPageInsightsOptions,
): {
  data: RunsPageInsightsData | undefined
  isError?: boolean
  isLoading: boolean
} {
  const { analysisWindowDays, enabled = true, refetchInterval } = options ?? {}
  const input = useMemo(() => {
    const transportFilters = buildRunsInsightsTransportFilters(filters)
    return transportFilters
      ? {
          filters: transportFilters,
          analysis_window_days: analysisWindowDays,
        }
      : null
  }, [analysisWindowDays, filters])

  const {
    data: response,
    isError,
    isLoading,
  } = useRunsPageInsightsTransport({
    enabled,
    refetchInterval,
    request: input,
  })

  const data = useMemo<RunsPageInsightsData | undefined>(
    () =>
      input && response ? mapRunsPageInsightsResponse(response) : undefined,
    [input, response],
  )

  return { data, isError, isLoading }
}
